import { EOwnerDeclaredIncome, EOwnerDeclaredRevenue } from 'common/banking.interface';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  FormControl,
  CircularProgress,
  MenuItem,
  Stack,
} from '@mui/material';
import { Formik, Form, FieldArray, FastField, useFormikContext } from 'formik';
import CustomPhoneField from 'components/CustomPhoneField';
import * as yup from 'yup';
import React from 'react';
import CustomDatePickerFormik from 'components/CustomDatePickerFormik';
import { DateTime } from 'luxon';
import {
  CreateBankingAccountParticipant,
  EParticipantType,
  EPrePartnerType,
  getPartnerKey,
  IPrePartner,
} from 'common/create-banking-account.dto';
import { maskCpf } from 'utils/banking';
import { useSnackbar } from 'notistack';

const ownerRevenueOptions = [
  { value: EOwnerDeclaredRevenue.UP_TO_81000, label: 'Até R$ 81.000 (MEI)' },
  { value: EOwnerDeclaredRevenue.FROM_81001_TO_360000, label: 'R$ 81.001 – R$ 360.000 (Microempresa)' },
  { value: EOwnerDeclaredRevenue.FROM_360001_TO_4800000, label: 'R$ 360.001 – R$ 4.800.000 (EPP)' },
  { value: EOwnerDeclaredRevenue.FROM_4800001_TO_78000000, label: 'R$ 4.800.001 – R$ 78.000.000 (Média Empresa)' },
  { value: EOwnerDeclaredRevenue.ABOVE_78000000, label: 'Acima de R$ 78.000.000 (Grande Empresa)' },
];

const ownerIncomeOptions = [
  { value: EOwnerDeclaredIncome.UP_TO_5000, label: 'Até R$ 5.000' },
  { value: EOwnerDeclaredIncome.FROM_5001_TO_10000, label: 'R$ 5.001 – R$ 10.000' },
  { value: EOwnerDeclaredIncome.FROM_10001_TO_30000, label: 'R$ 10.001 – R$ 30.000' },
  { value: EOwnerDeclaredIncome.FROM_30001_TO_100000, label: 'R$ 30.001 – R$ 100.000' },
  { value: EOwnerDeclaredIncome.ABOVE_100000, label: 'Acima de R$ 100.000' },
];

interface ParticipantForm {
  name?: string;
  document?: string;
  socialName: string;
  birthDate: string;
  motherName: string;
  phoneNumber: string;
  email: string;
  pep: string;
  addressPostalCode: string;
  addressStreet: string;
  addressNumber: string;
  addressComplement: string;
  addressNeighborhood: string;
  addressCity: string;
  addressState: string;
  revenue: string;
}

interface Props {
  partners: IPrePartner[];
  onBack: () => void;
  onFinish: (participants: CreateBankingAccountParticipant[]) => void | Promise<void>;
  onSyncPartners: (partners: IPrePartner[]) => void;
  submitting: boolean;
}

interface FormValues {
  participants: ParticipantForm[];
}

const participantSchema = yup.object({
  socialName: yup.string().trim().max(200, 'Máximo 200 caracteres'),
  birthDate: yup
    .string()
    .trim()
    .required('Data de nascimento é obrigatória')
    .max(10, 'Máximo 10 caracteres')
    .test('valid-date', 'Data de nascimento inválida', (value) => Boolean(value && DateTime.fromFormat(value, 'dd/MM/yyyy').isValid)),
  motherName: yup.string().trim().required('Nome da mãe é obrigatório').max(200, 'Máximo 200 caracteres'),
  phoneNumber: yup.string().trim().required('Celular é obrigatório').max(20, 'Máximo 20 caracteres'),
  email: yup.string().trim().email('E-mail inválido').required('E-mail é obrigatório').max(255, 'Máximo 255 caracteres'),
  pep: yup.string().required('Campo obrigatório'),
  addressPostalCode: yup.string().trim().required('CEP é obrigatório').max(10, 'Máximo 10 caracteres'),
  addressStreet: yup.string().trim().required('Rua é obrigatória').max(255, 'Máximo 255 caracteres'),
  addressNumber: yup.string().trim().required('Número é obrigatório').max(10, 'Máximo 10 caracteres'),
  addressComplement: yup.string().trim().max(100, 'Máximo 100 caracteres'),
  addressNeighborhood: yup.string().trim().required('Bairro é obrigatório').max(100, 'Máximo 100 caracteres'),
  addressCity: yup.string().trim().required('Cidade é obrigatória').max(100, 'Máximo 100 caracteres'),
  addressState: yup.string().trim().required('Estado é obrigatório').max(2, 'Máximo 2 caracteres'),
  revenue: yup.string().required('Faturamento é obrigatório'),
});

const validationSchema = yup.object({
  participants: yup.array().of(participantSchema).required(),
});

function buildInitialValues(partners: IPrePartner[]): FormValues {
  return {
    participants: partners.map((p) => ({
      name: p.name || '',
      document: p.document || '',
      socialName: p.socialName || '',
      birthDate: p.birthDate || '',
      motherName: p.motherName || '',
      phoneNumber: p.phoneNumber || '',
      email: p.email || '',
      pep: p.pep || 'nao',
      addressPostalCode: p.addressPostalCode || '',
      addressStreet: p.addressStreet || '',
      addressNumber: p.addressNumber || '',
      addressComplement: p.addressComplement || '',
      addressNeighborhood: p.addressNeighborhood || '',
      addressCity: p.addressCity || '',
      addressState: p.addressState || '',
      revenue: p.revenue || '',
    })),
  };
}

function formatBirthDate(value?: string): string {
  if (!value) return '';
  const parsed = DateTime.fromFormat(value, 'dd/MM/yyyy');
  return parsed.isValid ? parsed.toFormat('yyyy-MM-dd') : '';
}

function mergePartnerFinishValues(partner: IPrePartner, values?: ParticipantForm): IPrePartner {
  if (!values) return partner;

  return {
    ...partner,
    socialName: values.socialName,
    birthDate: values.birthDate,
    motherName: values.motherName,
    phoneNumber: values.phoneNumber,
    email: values.email,
    pep: values.pep,
    addressPostalCode: values.addressPostalCode,
    addressStreet: values.addressStreet,
    addressNumber: values.addressNumber,
    addressComplement: values.addressComplement,
    addressNeighborhood: values.addressNeighborhood,
    addressCity: values.addressCity,
    addressState: values.addressState,
    revenue: values.revenue,
    ...(partner.type === EPrePartnerType.LEGAL
      ? {
          name: values.name || partner.name,
          document: values.document || partner.document,
        }
      : {}),
  };
}

function buildOutput(partners: IPrePartner[], values: FormValues): CreateBankingAccountParticipant[] {
  return partners
    .map((partner, index) => ({ partner, formValues: values.participants[index] }))
    .sort((a, b) => Number(b.partner.responsible) - Number(a.partner.responsible))
    .map(({ partner, formValues: v }) => {
      if (!v) {
        throw new Error('Missing participant form values');
      }

      return {
        type: partner.legalRepresentative ? EParticipantType.OWNER : EParticipantType.ASSOCIATE,
        fullName: partner.type === EPrePartnerType.PHYSICAL ? partner.name : String(v.name),
        socialName: v.socialName,
        birthDate: formatBirthDate(v.birthDate),
        documentNumber: partner.type === EPrePartnerType.PHYSICAL ? partner.document : String(v.document),
        motherName: v.motherName,
        phoneNumber: v.phoneNumber,
        email: v.email,
        pep: v.pep === 'sim',
        addressPostalCode: v.addressPostalCode,
        addressStreet: v.addressStreet,
        addressNumber: v.addressNumber,
        addressComplement: v.addressComplement,
        addressNeighborhood: v.addressNeighborhood,
        addressCity: v.addressCity,
        addressState: v.addressState,
        revenue: v.revenue,
      };
    });
}

const RedAsterisk = () => <span style={{ color: '#d32f2f', marginLeft: 2 }}>*</span>;

const requiredFields = new Set([
  'birthDate',
  'motherName',
  'phoneNumber',
  'email',
  'pep',
  'addressPostalCode',
  'addressStreet',
  'addressNumber',
  'addressNeighborhood',
  'addressCity',
  'addressState',
  'revenue',
]);

function labelWithAsterisk(label: string, field: string) {
  return requiredFields.has(field) ? (
    <>
      {label}
      <RedAsterisk />
    </>
  ) : (
    label
  );
}

const ParticipantCard = React.memo(({ partner, index, loading }: { partner: IPrePartner; index: number; loading?: boolean }) => {
  const base = `participants.${index}`;
  const { setFieldValue } = useFormikContext<FormValues>();

  // Type-safe debounce reference (works in browser + Node typings)
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchAddressFromViaCep = React.useCallback(
    async (cep: string) => {
      try {
        const cleanCep = cep.replace(/\D/g, '');
        if (cleanCep.length !== 8) return;

        const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await response.json();

        if (data.erro) return;

        // Update only this participant
        setFieldValue(`${base}.addressStreet`, data.logradouro || '');
        setFieldValue(`${base}.addressNeighborhood`, data.bairro || '');
        setFieldValue(`${base}.addressCity`, data.localidade || '');
        setFieldValue(`${base}.addressState`, data.uf || '');
      } catch {
        // Silent fail
      }
    },
    [setFieldValue, base]
  );

  const renderTextField = (name: string, label: string) => {
    const placeholders: Record<string, string> = {
      birthDate: 'dd/mm/aaaa',
      phoneNumber: '(DDD) 0 0000 - 0000',
      email: 'email@email.com.br',
      motherName: 'Nome da mãe',
      addressPostalCode: '000000-000',
      addressState: 'Selecione o estado',
      addressCity: 'Digite a cidade da empresa',
      addressNeighborhood: 'Nome do bairro',
      addressStreet: 'Rua, avenida, etc.',
      addressNumber: '0000',
      addressComplement: 'Ex: Apartamento, casa',
      revenue: 'R$ 0,00',
    };

    if (name === 'phoneNumber') {
      return (
        <FastField name={`${base}.phoneNumber`}>
          {({ field, meta, form }) => (
            <CustomPhoneField
              name={field.name}
              label={labelWithAsterisk(label, name)}
              value={field.value}
              placeholder={placeholders[name] || ''}
              onChange={(e: any) => form.setFieldValue(field.name, e.target.value)}
              error={Boolean(meta.touched && meta.error)}
              helperText={meta.touched && meta.error ? String(meta.error) : ''}
              fullWidth
            />
          )}
        </FastField>
      );
    }

    if (name === 'revenue') {
      // CPF partner: select for income
      if (partner.type === EPrePartnerType.PHYSICAL) {
        return (
          <FastField name={`${base}.revenue`}>
            {({ field, meta, form }) => (
              <TextField
                {...field}
                select
                label={labelWithAsterisk(`Faixa de renda mensal`, name)}
                fullWidth
                error={Boolean(meta.touched && meta.error)}
                helperText={meta.touched && meta.error}
                variant="standard"
                placeholder={placeholders[name] || ''}
                onChange={(e) => form.setFieldValue(field.name, e.target.value)}
              >
                {ownerIncomeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </FastField>
        );
      }
      // PJ partner: select for revenue
      return (
        <FastField name={`${base}.revenue`}>
          {({ field, meta, form }) => (
            <TextField
              {...field}
              select
              label={labelWithAsterisk('Faixa de renda anual', name)}
              fullWidth
              error={Boolean(meta.touched && meta.error)}
              helperText={meta.touched && meta.error}
              variant="standard"
              placeholder={placeholders[name] || ''}
              onChange={(e) => form.setFieldValue(field.name, e.target.value)}
            >
              {ownerRevenueOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        </FastField>
      );
    }

    return (
      <FastField name={`${base}.${name}`}>
        {({ field, meta }) => (
          <TextField
            {...field}
            label={labelWithAsterisk(label, name)}
            fullWidth
            error={Boolean(meta.touched && meta.error)}
            helperText={meta.touched && meta.error}
            variant="standard"
            placeholder={placeholders[name] || ''}
          />
        )}
      </FastField>
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: '12px', position: 'relative', overflow: 'hidden' }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(255,255,255,0.6)',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'all',
          }}
        />
      )}

      <Stack
        direction="row"
        bgcolor="#F2F4F8"
        borderRadius="8px"
        p={2}
        mb={2}
        sx={{
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography fontSize={22} fontWeight={600} color="grey.800">
            {partner.type === EPrePartnerType.LEGAL ? partner.companyName : partner.name}
          </Typography>

          <Typography fontSize={14} color="text.secondary">
            Documento:
            <Box ml={0.5} component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>
              {partner.type === EPrePartnerType.LEGAL ? partner.cnpj : partner.document}
            </Box>
          </Typography>
        </Box>

        <Stack spacing={1}>
          <Box display="flex" justifyContent="right">
            <Chip
              label={partner.legalRepresentative ? 'Representante Legal' : 'Sócio'}
              size="small"
              variant="outlined"
              sx={{
                bgcolor: (theme) => theme.palette.primary.light,
                borderColor: (theme) => theme.palette.primary.main,
                color: () => 'white',
                fontWeight: 500,
              }}
            />
          </Box>

          {partner.responsible && (
            <Box>
              <Chip
                label="Responsável por abertura"
                size="small"
                sx={{
                  bgcolor: (theme) => theme.palette.info.light,
                  borderColor: (theme) => theme.palette.info.main,
                  color: () => 'white',
                  fontWeight: 500,
                }}
              />
            </Box>
          )}
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
          gap: 3,
          mb: 3,
        }}
      >
        {partner.type === EPrePartnerType.LEGAL && (
          <>
            <Box sx={{ gridColumn: '1 / 3' }}>
              <FastField name={`${base}.name`}>
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    label={
                      <>
                        Nome do representante dessa empresa
                        <RedAsterisk />
                      </>
                    }
                    placeholder="Nome do representante dessa empresa"
                    variant="standard"
                    fullWidth
                    error={Boolean(meta.touched && meta.error)}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </FastField>
            </Box>

            <Box>
              <FastField name={`${base}.document`}>
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    label="CPF do representante"
                    placeholder="000.000.000-00"
                    variant="standard"
                    fullWidth
                    onChange={(e) => {
                      let input = e.target.value;
                      if (input.length > 14) input = input.slice(0, 14);
                      setFieldValue(field.name, maskCpf(input));
                    }}
                    error={Boolean(meta.touched && meta.error)}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </FastField>
            </Box>
          </>
        )}

        <FastField name={`${base}.birthDate`}>
          {({ field, form, meta }) => (
            <CustomDatePickerFormik
              name={field.name}
              value={field.value ? DateTime.fromFormat(field.value, 'dd/MM/yyyy') : null}
              onChange={(date: any) => {
                let formatted = '';
                if (date && typeof date === 'object' && date.isValid) {
                  formatted = date.toFormat('dd/MM/yyyy');
                } else if (typeof date === 'string') {
                  formatted = date;
                }
                form.setFieldValue(field.name, formatted);
              }}
              format="dd/MM/yyyy"
              textFieldProps={{
                variant: 'standard',
                label: labelWithAsterisk('Data de nascimento', 'birthDate'),
                placeholder: 'dd/mm/aaaa',
                error: Boolean(meta.touched && meta.error),
                helperText: meta.touched && meta.error,
                fullWidth: true,
              }}
              inputProps={{ inputMode: 'text' }}
            />
          )}
        </FastField>

        {renderTextField('phoneNumber', 'Celular')}

        {renderTextField('email', 'E-mail')}

        <Box sx={{ gridColumn: '1 / -1' }}>{renderTextField('motherName', 'Nome da mãe')}</Box>

        <Box sx={{ gridColumn: '1 / -1' }}>{renderTextField('revenue', 'Faixa de renda')}</Box>

        <Box sx={{ gridColumn: '1 / -1' }}>
          <FastField name={`${base}.pep`}>
            {({ field }) => (
              <FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormLabel sx={{ fontSize: 16, mb: 0, mr: 2, minWidth: 220 }}>
                    Trata-se de uma pessoa exposta politicamente?
                    <RedAsterisk />
                  </FormLabel>
                  <RadioGroup row {...field}>
                    <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                    <FormControlLabel value="nao" control={<Radio />} label="Não" />
                  </RadioGroup>
                </Box>
              </FormControl>
            )}
          </FastField>
        </Box>
      </Box>

      <Typography fontWeight={600} fontSize={16} sx={{ mb: 2 }}>
        Endereço
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
          gap: 3,
        }}
      >
        <FastField name={`${base}.addressPostalCode`}>
          {({ field, meta }) => {
            const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
              field.onChange(e);

              if (debounceRef.current) {
                clearTimeout(debounceRef.current);
              }

              debounceRef.current = setTimeout(() => {
                const cleanCep = e.target.value.replace(/\D/g, '');
                if (cleanCep.length === 8) {
                  fetchAddressFromViaCep(e.target.value);
                }
              }, 400);
            };

            return (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <TextField
                  {...field}
                  label="CEP"
                  fullWidth
                  onChange={handleZipCodeChange}
                  error={Boolean(meta.touched && meta.error)}
                  helperText={meta.touched && meta.error}
                  variant="standard"
                  placeholder="000000-000"
                />
              </Box>
            );
          }}
        </FastField>
        {renderTextField('addressState', 'Estado')}
        {renderTextField('addressCity', 'Cidade')}
        {renderTextField('addressNeighborhood', 'Bairro')}
        {renderTextField('addressStreet', 'Logradouro')}
        {renderTextField('addressNumber', 'Número')}
        {renderTextField('addressComplement', 'Complemento (opcional)')}
      </Box>
    </Paper>
  );
});

const PartnerFinish = ({ partners, onBack, onFinish, onSyncPartners, submitting }: Props) => {
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (values: FormValues) => {
    let payload: CreateBankingAccountParticipant[];

    try {
      payload = buildOutput(partners, values);
    } catch (_err) {
      enqueueSnackbar('Ocorreu um erro ao preparar os dados. Verifique as informações e tente novamente.', { variant: 'error' });
      return;
    }

    await onFinish(payload);
  };

  return (
    <Box>
      <Typography fontWeight={500} fontSize={20} mt={6} mb={4}>
        Complete os dados cadastrais necessários
      </Typography>

      <Formik<FormValues>
        initialValues={buildInitialValues(partners)}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur
        onSubmit={handleSubmit}
      >
        {({ values }) => (
          <Form>
            <FieldArray name="participants">
              {() => (
                <>
                  {partners.map((partner, idx) => (
                    <ParticipantCard key={getPartnerKey(partner)} partner={partner} index={idx} loading={submitting} />
                  ))}
                </>
              )}
            </FieldArray>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  onSyncPartners(partners.map((p, idx) => mergePartnerFinishValues(p, values.participants[idx])));
                  onBack();
                }}
                sx={{ borderRadius: 24 }}
                disabled={submitting}
              >
                Voltar
              </Button>

              <Button type="submit" variant="contained" sx={{ borderRadius: 24 }} disabled={submitting}>
                {submitting ? <CircularProgress size={24} color="inherit" /> : 'Finalizar'}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default PartnerFinish;
